import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from "../../App";

// Visually hidden input for file upload button
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Main modal component
const ModalComponent = ({ ModelOpen, onClose }) => {
  const [ErrorText, setErrorText] = useState(null);
  const [files, setFiles] = useState([]); // Changed to an array for multiple files
  const [filePreviews, setFilePreviews] = useState([]); // Preview for multiple images
  const [open, setOpen] = useState(false);
  const [WarnMSG, setWarnMSG] = useState(null);
  const navigate = useNavigate();
  
  const login_id = localStorage.getItem("LOGIN_ID");

  useEffect(() => {
    // Reset state when the modal opens
    setErrorText(null);
    setFiles([]);
    setWarnMSG(null);
    setFilePreviews([]);
  }, [ModelOpen]);  // This will run every time the modal opens

  useEffect(() => {
    // Create previews for the files
    const previews = files.map(file => URL.createObjectURL(file));
    setFilePreviews(previews);
  }, [files]);

  const handleFileChange = (event) => {
    const uploadedFiles = Array.from(event.target.files); // Get an array of files
    if (uploadedFiles.length) {
      setFiles(prevFiles => [...prevFiles, ...uploadedFiles]); // Append new files to the current list
    }
  };

  const handleRemoveImage = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index)); // Remove specific file by index
  };

  const HandleConfirm = () => {
    setOpen(true);
    if (!ErrorText) {
      setWarnMSG("Enter The Issue!");
      setOpen(false);
      return;
    }

    const formData = new FormData();
    formData.append('type', 'InsertErrorReport');
    formData.append('User_Id', login_id);
    formData.append('ErrorText', ErrorText);

    // Append files to form data
    files.forEach(file => {formData.append('file[]', file)});

    axios.post(BASE_URL + '/siso', formData)
      .then(response => {
        if (response.status === 200) {
          Swal.fire({
            icon: "success",
            text: 'Error Report submitted successfully!',
          });
          onClose(); // Close the modal after success
        } else {
          Swal.fire({
            icon: "warning",
            text: 'Unexpected response status. Please try again.',
          });
        }
      })
      .catch(error => {
        if (error.response) {
          Swal.fire({
            icon: "warning",
            text: `Error: ${error.response.data.message}`,
          });
          navigate('/Home/Spares/');
        } else if (error.request) {
          navigate('/Home/Spares/');
          console.error('No response received');
        } else {
          console.error('Error setting up the request:', error.message);
        }
      })
      .finally(() => {
        setOpen(false);
      });
  };

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog open={ModelOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>Report An Error or Issue</DialogTitle>
        <DialogContent>
          <Box sx={{ padding: 2 }}>
            {/* Text field for user input */}
            <TextField
              id="outlined-multiline-flexible"
              label="Describe your issue"
              fullWidth
              size="small"
              multiline
              maxRows={20}
              value={ErrorText}
              onChange={(e) => {
                setErrorText(e.target.value);
                setWarnMSG(null);
              }}
            />
            {WarnMSG && (<center><span className='text-danger'>{WarnMSG}</span></center>)}

            {/* Upload button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 2 }}>
              <Button
                sx={{ marginTop: 2 }}
                component="label"
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                sx={{
                  padding: '10px 20px',
                  textTransform: 'none',
                  backgroundColor: '#1976d2', // Custom color
                  '&:hover': { backgroundColor: '#1565c0' },
                }}
              >
                Upload Files
                <VisuallyHiddenInput
                  type="file"
                  onChange={handleFileChange}
                  multiple
                />
              </Button>
            </Box>

            {/* Display the uploaded images */}
            {filePreviews.length > 0 && (
              <Box sx={{ textAlign: 'center', marginTop: 2 }}>
                {filePreviews.map((preview, index) => (
                  <Box key={index} sx={{ marginBottom: 2 }}>
                    <img
                      src={preview}
                      alt={`Uploaded ${index + 1}`}
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        marginTop: 2,
                        textTransform: 'none',
                        backgroundColor: '#f44336', // Red background
                        color: '#fff',
                        '&:hover': { backgroundColor: '#d32f2f' },
                      }}
                    >
                      Remove Image
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={onClose} color="secondary" variant="outlined" sx={{ marginRight: 1 }}>Close</Button>
          <Button onClick={HandleConfirm} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalComponent;
